/**
 * Task #2173 — NOC SIP Cluster Round A: schemi Zod co-located ai type TS in
 * `packages/shared/src/types/noc-runtime.ts`.
 *
 * Usati al boundary HTTP (BE → FE) per validare le response e dal client per
 * parsing tipizzato (`z.infer<>` deriva il type da qui — i type TS in
 * `noc-runtime.ts` sono allineati manualmente, vedi commento in fondo per
 * gli `assert` di compatibilità).
 *
 * I body inviati dal client per questi endpoint sono limitati a query string
 * (limit, nodeId, htableName, jailPrefix) — gli schema delle query sono
 * esportati a fondo file (`*QuerySchema`) e usati dalla route con
 * `validate({ query: ... })`.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------

const nocReasonSchema = z.string().nullable();

const nocRpcTargetSchema = <T extends z.ZodTypeAny>(inner: T) =>
  z.object({
    target: z.string(),
    ok: z.boolean(),
    error: z.string().nullable(),
    data: inner.nullable(),
  });

// ---------------------------------------------------------------------------
// Kamailio Core
// ---------------------------------------------------------------------------

export const kamailioTmStatsSchema = z.object({
  received: z.number().nonnegative(),
  relayed: z.number().nonnegative(),
  localGenerated: z.number().nonnegative(),
  current: z.number().nonnegative(),
  waiting: z.number().nonnegative(),
  totalLocal: z.number().nonnegative(),
  replied: z.number().nonnegative(),
  response2xx: z.number().nonnegative(),
  response3xx: z.number().nonnegative(),
  response4xx: z.number().nonnegative(),
  response5xx: z.number().nonnegative(),
  response6xx: z.number().nonnegative(),
});

export const kamailioSlStatsSchema = z.object({
  tx1xx: z.number().nonnegative(),
  tx2xx: z.number().nonnegative(),
  tx3xx: z.number().nonnegative(),
  tx4xx: z.number().nonnegative(),
  tx5xx: z.number().nonnegative(),
  tx6xx: z.number().nonnegative(),
  failures: z.number().nonnegative(),
  sentReplies: z.number().nonnegative(),
  sentErrReplies: z.number().nonnegative(),
  receivedAck: z.number().nonnegative(),
});

export const kamailioMemStatsSchema = z.object({
  total: z.number().nonnegative(),
  free: z.number().nonnegative(),
  used: z.number().nonnegative(),
  realUsed: z.number().nonnegative(),
  usagePct: z.number().min(0).max(100),
});

export const kamailioCoreNodeSnapshotSchema = z.object({
  target: z.string(),
  ok: z.boolean(),
  error: z.string().nullable(),
  tm: kamailioTmStatsSchema.nullable(),
  sl: kamailioSlStatsSchema.nullable(),
  shmem: kamailioMemStatsSchema.nullable(),
  pkgmem: kamailioMemStatsSchema.nullable(),
});

export const kamailioCoreSnapshotSchema = z.object({
  degraded: z.boolean(),
  reason: nocReasonSchema,
  perTarget: z.array(kamailioCoreNodeSnapshotSchema).readonly(),
});

// ---------------------------------------------------------------------------
// AOR
// ---------------------------------------------------------------------------

export const aorContactRowSchema = z.object({
  contactUri: z.string(),
  path: z.string(),
  receivedAtMs: z.number().nonnegative(),
  expiresAtMs: z.number().nonnegative(),
  userAgent: z.string(),
  socketReceived: z.string(),
});

export const aorRowSchema = z.object({
  aor: z.string(),
  domain: z.string(),
  contactsCount: z.number().int().nonnegative(),
  contacts: z.array(aorContactRowSchema).readonly(),
});

export const aorNodeSnapshotSchema = z.object({
  target: z.string(),
  ok: z.boolean(),
  error: z.string().nullable(),
  totalAors: z.number().int().nonnegative(),
  totalContacts: z.number().int().nonnegative(),
  aors: z.array(aorRowSchema).readonly(),
});

export const aorSnapshotSchema = z.object({
  degraded: z.boolean(),
  reason: nocReasonSchema,
  perTarget: z.array(aorNodeSnapshotSchema).readonly(),
  totalAors: z.number().int().nonnegative(),
  totalContacts: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// htable
// ---------------------------------------------------------------------------

export const htableValueKindSchema = z.enum(['string', 'int', 'bytes', 'unknown']);

export const htableEntryRowSchema = z.object({
  key: z.string(),
  value: z.string(),
  valueKind: htableValueKindSchema,
  expiresAtMs: z.number().nullable(),
});

export const htableNodeSnapshotSchema = z.object({
  target: z.string(),
  ok: z.boolean(),
  error: z.string().nullable(),
  size: z.number().int().nonnegative(),
  entries: z.array(htableEntryRowSchema).readonly(),
  sentinels: z.array(z.string()).readonly(),
});

export const htableSnapshotSchema = z.object({
  degraded: z.boolean(),
  reason: nocReasonSchema,
  htableName: z.string(),
  perTarget: z.array(htableNodeSnapshotSchema).readonly(),
});

// ---------------------------------------------------------------------------
// permissions counters
// ---------------------------------------------------------------------------
// Task #2206 — `secfilterCounters*` rimossi (mod_secfilter dismesso).

export const permissionsCountersNodeSnapshotSchema = z.object({
  target: z.string(),
  ok: z.boolean(),
  error: z.string().nullable(),
  counters: z.record(z.string(), z.number()),
  allowHits: z.number().nonnegative(),
  denyHits: z.number().nonnegative(),
});

export const permissionsCountersSnapshotSchema = z.object({
  degraded: z.boolean(),
  reason: nocReasonSchema,
  perTarget: z.array(permissionsCountersNodeSnapshotSchema).readonly(),
});

// ---------------------------------------------------------------------------
// rtpengine ports
// ---------------------------------------------------------------------------

export const rtpenginePortRangeSchema = z.object({
  min: z.number().int().positive(),
  max: z.number().int().positive(),
  used: z.number().int().nonnegative(),
  free: z.number().int().nonnegative(),
  usagePct: z.number().min(0).max(100),
});

export const rtpengineStatisticsSchema = z.object({
  currentSessions: z.number().int().nonnegative(),
  totalSessions: z.number().nonnegative(),
  relayedPackets: z.number().nonnegative(),
  relayedBytes: z.number().nonnegative(),
  errors: z.number().nonnegative(),
});

export const rtpenginePortsSnapshotSchema = z.object({
  degraded: z.boolean(),
  reason: nocReasonSchema,
  host: z.string(),
  portRange: rtpenginePortRangeSchema,
  statistics: rtpengineStatisticsSchema,
  polledAtMs: z.number().nonnegative(),
});

export const fsRtpPortsSnapshotSchema = z.object({
  degraded: z.boolean(),
  reason: nocReasonSchema,
  nodeId: z.enum(['fs1', 'fs2']),
  activeChannels: z.number().int().nonnegative(),
  portRange: rtpenginePortRangeSchema,
  polledAtMs: z.number().nonnegative(),
});

// ---------------------------------------------------------------------------
// fail2ban NOC snapshot
// ---------------------------------------------------------------------------

export const fail2BanJailAggregateCellSchema = z.object({
  jail: z.string(),
  currentlyBanned: z.number().int().nonnegative(),
  totalBanned: z.number().int().nonnegative(),
  enabledOnNodes: z.array(z.string()).readonly(),
});

export const fail2BanNocSnapshotSchema = z.object({
  degraded: z.boolean(),
  reason: nocReasonSchema,
  jails: z.array(fail2BanJailAggregateCellSchema).readonly(),
  totalCurrentlyBanned: z.number().int().nonnegative(),
  totalEverBanned: z.number().int().nonnegative(),
  nodesOnline: z.number().int().nonnegative(),
  nodesTotal: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// SIP tap
// ---------------------------------------------------------------------------

export const sipTapDirectionSchema = z.enum(['in', 'out']);
export const sipTapTransportSchema = z.enum(['udp', 'tcp', 'tls', 'wss', 'ws']);

export const sipTapSdpInfoSchema = z.object({
  mediaLines: z.number().int().nonnegative(),
  codecs: z.array(z.string()).readonly(),
});

export const sipTapEntrySchema = z.object({
  id: z.string(),
  ts: z.string(),
  direction: sipTapDirectionSchema,
  transport: sipTapTransportSchema,
  srcIp: z.string(),
  dstIp: z.string(),
  method: z.string().nullable(),
  respCode: z.number().int().nullable(),
  callId: z.string(),
  cseq: z.string(),
  via: z.array(z.string()).readonly(),
  maxForwards: z.number().int().nullable(),
  contentType: z.string().nullable(),
  userAgent: z.string().nullable(),
  allow: z.array(z.string()).readonly(),
  supported: z.array(z.string()).readonly(),
  toMasked: z.string(),
  fromMasked: z.string(),
  paiMasked: z.string().nullable(),
  rpidMasked: z.string().nullable(),
  contactMasked: z.string().nullable(),
  sdp: sipTapSdpInfoSchema.nullable(),
  maskingApplied: z.boolean(),
});

export const sipTapSnapshotSchema = z.object({
  degraded: z.boolean(),
  reason: nocReasonSchema,
  entries: z.array(sipTapEntrySchema).readonly(),
  latestTsMs: z.number().nullable(),
  ttlSec: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Query schemas (input al boundary HTTP — usati con `validate({ query })`)
// ---------------------------------------------------------------------------

const positiveIntFromString = (max: number) =>
  z
    .string()
    .optional()
    .transform((s) => (s === undefined || s === '' ? undefined : Number.parseInt(s, 10)))
    .refine((n) => n === undefined || (Number.isInteger(n) && n > 0 && n <= max), {
      message: `Atteso intero positivo ≤ ${max}`,
    });

export const nocLimitQuerySchema = z.object({
  limit: positiveIntFromString(500),
});

export const nocHtableParamsSchema = z.object({
  name: z.enum(['kam_valid_users', 'kam_attackers', 'tenants', 'dids']),
});

export const nocFsNodeParamsSchema = z.object({
  nodeId: z.enum(['fs1', 'fs2']),
});

export const nocTapQuerySchema = z.object({
  limit: positiveIntFromString(200),
});

// ---------------------------------------------------------------------------
// Note di compatibilità type ↔ schema
//
// I tipi in `noc-runtime.ts` sono allineati manualmente con gli schema sopra.
// Per evitare drift silenzioso, le route backend usano `z.infer<typeof
// <schema>>` per le response e l'import dei tipi serve solo lato consumer
// (FE). Test futuri possono aggiungere `expectTypeOf` di equivalenza, fuori
// scope di Round A.
// ---------------------------------------------------------------------------

export type KamailioCoreSnapshotInferred = z.infer<typeof kamailioCoreSnapshotSchema>;
export type AorSnapshotInferred = z.infer<typeof aorSnapshotSchema>;
export type HtableSnapshotInferred = z.infer<typeof htableSnapshotSchema>;
export type PermissionsCountersSnapshotInferred = z.infer<typeof permissionsCountersSnapshotSchema>;
export type RtpenginePortsSnapshotInferred = z.infer<typeof rtpenginePortsSnapshotSchema>;
export type FsRtpPortsSnapshotInferred = z.infer<typeof fsRtpPortsSnapshotSchema>;
export type Fail2BanNocSnapshotInferred = z.infer<typeof fail2BanNocSnapshotSchema>;
export type SipTapSnapshotInferred = z.infer<typeof sipTapSnapshotSchema>;

// Avoid runtime-only namespace; consumer typescript will import named schemas.
export {
  nocRpcTargetSchema as __nocRpcTargetSchema,
  nocReasonSchema as __nocReasonSchema,
};

// ---------------------------------------------------------------------------
// Task #2179 — Capacity thresholds (Round C1)
// ---------------------------------------------------------------------------

export const CapacityThresholdPairSchema = z.object({
  warnPct: z.number().min(0).max(100),
  criticalPct: z.number().min(0).max(100),
}).refine((v) => v.warnPct < v.criticalPct, {
  message: 'warnPct deve essere strettamente minore di criticalPct',
  path: ['warnPct'],
});

export const CapacityThresholdsSchema = z.object({
  rtpengine: CapacityThresholdPairSchema,
  fs: CapacityThresholdPairSchema,
});
